import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Facebook, Instagram } from "lucide-react";
import Image1 from "./assets/sadie1.jpg";
import Image2 from "./assets/sadie2.jpg";
import im13 from './assets/im13.jpeg';
import Video1 from "./assets/IMG_5820.mp4";
import AboutModal from "./AboutModal";

const mediaItems = [Image1, Image2, im13, Video1];

const PictureTextBlock = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isMobileOrTablet, setIsMobileOrTablet] = useState(
        window.innerWidth <= 1024
    );


    // 📱 resize
    useEffect(() => {
        const handleResize = () => setIsMobileOrTablet(window.innerWidth <= 1024);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);



    // 🖼 auto rotate (desktop only)
    useEffect(() => {
        if (isMobileOrTablet) return;

        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % mediaItems.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [isMobileOrTablet]);

    useEffect(() => {
        if (!isModalOpen) return;

        const body = document.body;
        const scrollY = window.scrollY;

        // Lock the body in place visually, but allow modal content to scroll
        body.style.position = "fixed";
        body.style.top = `-${scrollY}px`;
        body.style.left = "0";
        body.style.right = "0";
        body.style.width = "100%";
        body.style.overflow = "hidden"; // ✅ prevents background scroll
        // ❌ remove touchAction and preventScroll — they block modal scrolling

        return () => {
            const storedY = parseInt(body.style.top || "0", 10) * -1;
            body.style.position = "";
            body.style.top = "";
            body.style.left = "";
            body.style.right = "";
            body.style.width = "";
            body.style.overflow = "";
            window.scrollTo(0, storedY);
        };
    }, [isModalOpen]);




    const styles = {
        container: {
            display: "flex",
            alignItems: isMobileOrTablet ? "center" : "center",
            justifyContent: isMobileOrTablet ? "center" : "flex-start",
            flexDirection: isMobileOrTablet ? "column" : "row", // ✅ stack vertically on mobile
            gap: isMobileOrTablet ? "40px" : "100px",
            flexWrap: "nowrap", // prevent weird wrapping on mobile
            margin: isMobileOrTablet ? "10px auto 50px" : "20px auto 80px",
            maxWidth: "1400px",
            padding: "0 5%",
            marginTop: isMobileOrTablet ? "30px" : "120px",
            boxSizing: "border-box",
        },
        mediaWrapper: {
            position: "relative",
            width: "550px",
            height: "550px",
            maxWidth: "100%",
            flexShrink: 0,
            borderRadius: "15px",
            overflow: "hidden",
            boxShadow: "0 6px 15px rgba(0,0,0,0.4)",
            marginTop: "40px",
        },
        mediaLayer: {
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "15px",
        },
        textContainer: {
            flex: 1,
            display: "flex",
            whiteSpace: "pre-line",
            flexDirection: "column",
            justifyContent: "center",
        },
        nameRow: {
            display: "flex",
            alignItems: "center",
            gap: "15px",
            flexWrap: "wrap",
            marginBottom: "20px",
        },
        title: {
            fontFamily: "'Libre Baskerville', serif",
            fontSize: "32px",
            fontWeight: 700,
            color: "#FFF2AF",
        },
        iconLink: {
            color: "#FFF2AF",
            transition: "transform 0.2s ease, color 0.2s ease",
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
        },
        text: {
            fontFamily: "'Libre Baskerville', serif",
            fontSize: "20px",
            fontWeight: 500,
            color: "#FFF2AF",
            lineHeight: 1.5,
            whiteSpace: "pre-line",
            marginBottom: "35px",
        },
        button: {
            fontFamily: "'Libre Baskerville', serif",
            fontSize: "18px",
            fontWeight: 600,
            color: "#000000",
            border: "none",
            borderRadius: "15px",
            padding: "12px 25px",
            cursor: "pointer",
            backgroundColor: "#8CCDEB",
            alignSelf: "flex-start",
            boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
        },
    };

    const handleHover = (e, scale) => {
        e.currentTarget.style.transform = `scale(${scale})`;
    };

    const adventure = {
        description: `Animals have always been my thing. I grew up in Moose Pass Alaska living in the same log cabin my entire life, and fortunate enough to have animals around me always. I’ve always gravitated towards them, and  some of my favorite memories as a kid involve taking care of the animals with my dad. So, I was a poop scooper long before it became a paid profession. I started working at Mitch Seaveys Ididaride Sled Dog Tours when I was 16 as a poop scooper—actually, I think the correct term is “handler,” but I’m just being real.
        
        The Seavey family and sled dogs weren’t new to me then, since my dad is longtime best friends with Mitch(3 time Iditarod champion) and his family. I spent a lot of time at their house as a kid. My dad was actually into sled dogs before I was born and ran a team of Mitch’s in the Iditarod in 1998, but that’s a story for another time.
        
        Now, I’ve spent  early a decade working at Ididaride, from handling and guiding and managing the business. It took about seven of those ten years before I decided to dedicate myself full time on a real dogsled. Winter is my favorite season, which is probably why I love mushing so much, along with hockey and snowboarding. Teaching snowboarding was actually somewhat of a career for me, while I lived in Colorado for several winters, but I set it aside to focus entirely on my dogs. I miss it, but it’s been a worthwhile sacrifice. Over the past few years, I’ve fallen in love with these dogs and this lifestyle.
        
        Aside from working and mushing, I love hiking mountains, fishing, playing music, and mountain unicycling. And yea, it’s just as it sounds—I simply hike a unicycle up a mountain and ride it down. I didn’t make it up; my wheel is specifically designed for it with tread and a break and everything. It’s awesome.  I’ll blame my mom for my love of the outdoors—she spends more time in the woods than at home(not an exaggeration). In fact, right now as I write this, she’s hiking to Barber Cabin after just two nights at home following a five-day through hike on Resurrection Pass Trail. She’s wild, and I love her for it.
        
        Back to the main point: I’m going to run the Iditarod. Honestly, I can hardly believe I am saying that, since it’s something I never really saw myself doing. The Iditarod truly brings out some tough individuals. I mentioned my dad ran it in 1998, and at the point in the race where he scratched(800 miles), he’d been traveling for 11 days, on not a single documented hour of sleep. It’s not that he didn’t try to rest; your mind becomes fanatical about your dogs, and won’t turn off when you’re solely responsible for yourself and them. That, plus sleep deprivation, hallucinations, storms, and temps reaching past negative 50 degrees Fahrenheit, sounds pretty miserable. So, why do it? Well, Iditarod is not about ticking a box and moving on; I only committed a few months ago after Mitch offered me something too special to decline. The real reason is I simply love my dogs, and I love mushing. These dogs love it even more, and training with these athletes—Alaskan Huskies(do some research on them they have superpowers)—  means experiencing a bond that goes far beyond what most people have with their house pets. I’ve had house dogs all my life, but this is something different. It’s just you and them out there, and that requires mutual respect and trust.  Over the hours, days, months, and years that we dedicate to these individuals, we build a loyalty and understanding that I haven’t found with many humans.
        
        But back to the Iditarod: this race has such a rich history, and it’s truly an honor to run it. I’m thrilled to share what I know will be one of the most beautiful, challenging, and spectacular experiences of my life, alongside teammates who are truly born for it, in the most beautiful place in the world. I hope you follow along, because this year is going to be pretty wild.`,
        images: [Image1],
    };

    const currentMedia = isMobileOrTablet ? Image1 : mediaItems[currentIndex];

    return (
        <div style={{ position: "relative" }}>
            <div style={styles.container}>
                <div style={styles.mediaWrapper}>
                    {/* Show just one static image on mobile */}
                    {isMobileOrTablet ? (
                        <img
                            src={Image1}
                            alt="Sadie"
                            style={{
                                ...styles.mediaLayer,
                                objectPosition: "center",
                            }}
                        />
                    ) : (
                        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                            <AnimatePresence mode="sync">
                                {currentMedia.endsWith(".mp4") ? (
                                    <motion.video
                                        key={currentMedia + "-" + currentIndex}
                                        src={currentMedia}
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        style={{
                                            ...styles.mediaLayer,
                                            position: 'absolute',
                                            top: 0,
                                            left: 0
                                        }}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{
                                            duration: 0.8,
                                            ease: "easeInOut"
                                        }}
                                    />
                                ) : (
                                    <motion.img
                                        key={currentMedia + "-" + currentIndex}
                                        src={currentMedia}
                                        alt="Sadie"
                                        style={{
                                            ...styles.mediaLayer,
                                            position: 'absolute',
                                            top: 0,
                                            left: 0
                                        }}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{
                                            duration: 0.8,
                                            ease: "easeInOut"
                                        }}
                                    />
                                )}
                            </AnimatePresence>
                        </div>
                    )}
                </div>

                {/* ✏️ Text + button */}
                <div style={styles.textContainer}>
                    <div style={styles.nameRow}>
                        <div style={styles.title}>Sadie Lindquist</div>
                        <a
                            href="https://www.facebook.com/share/16R1uic6rS/?mibextid=wwXIfr"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={styles.iconLink}
                            onMouseEnter={(e) => handleHover(e, 1.2)}
                            onMouseLeave={(e) => handleHover(e, 1.0)}
                        >
                            <Facebook size={28} />
                        </a>
                        <a
                            href="https://www.instagram.com/sadie_lindquist?igsh=Mm02NGFoZzFsMjJo&utm_source=qr"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={styles.iconLink}
                            onMouseEnter={(e) => handleHover(e, 1.2)}
                            onMouseLeave={(e) => handleHover(e, 1.0)}
                        >
                            <Instagram size={28} />
                        </a>
                    </div>

                    <div style={styles.text}>
                        Born and raised in Moose Pass, Alaska, I’ve spent many seasons directing sled dog tours. Now, my winters are fully dedicated to training and racing dogs out of three-time Iditarod champion Mitch Seavey’s kennel. This year, my purpose is to coach and train a phenomenal young team of Alaskan huskies, with the ultimate goal of running the 1,000-mile Iditarod sled dog race. I hope you’ll follow along on a pretty sweet journey this season!
                    </div>

                    <motion.button
                        style={styles.button}
                        whileHover={{
                            scale: 1.1,
                            backgroundColor: "#A2E4FF",
                            boxShadow: "0 6px 15px rgba(140,205,235,0.6)",
                        }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ type: "spring", stiffness: 300, damping: 12 }}
                        onClick={() => setIsModalOpen(true)}
                    >
                        More About Me
                    </motion.button>
                </div>
            </div>

            {isModalOpen && (
                <AboutModal adventure={adventure} onClose={() => setIsModalOpen(false)} />
            )}
        </div>
    );
};

export default PictureTextBlock;